import { registerComponent, createMessage } from "helpers";
import WYATTElement from "wyatt";
import StoreAvailability from "store-availability";

registerComponent(
  "quantity-validation",
  class QuantityValidation extends WYATTElement {
    constructor() {
      super();

      this.StoreAvailability = new StoreAvailability(this);

      this.input = this.querySelector("input");

      this.cartQuantity = 0;
      this.pendingCartMessageSync = false;
      this.lastClampedValue = null;
      this.validateGeneration = 0;

      this.flushCartMessageSync = this.flushCartMessageSync.bind(this);
      this.handleCartQuantityChange = this.handleCartQuantityChange.bind(this);
      this.handleCartStepperClick = this.handleCartStepperClick.bind(this);
      this.onPreferredStoreChange = this.handlePreferredStoreChange.bind(this);
      this.onCartUpdated = this.handleCartUpdated.bind(this);
      this.onUpdateDOM = this.handleUpdateDOM.bind(this);

      this.form.addEventListener("submit", this.handleSubmit.bind(this), true);

      if (this.isCartMode) {
        this.input.addEventListener(
          "change",
          this.handleCartQuantityChange,
          true,
        );
        this.addEventListener("click", this.handleCartStepperClick, true);
      }

      this.validate();
    }

    connectedCallback() {
      this.dataset.storeNumber = this.StoreAvailability.storeNumber;

      this.subscribe(
        WYATT.PubSub.events.preferredStoreChange,
        this.onPreferredStoreChange,
      );

      this.subscribe(WYATT.PubSub.events.cartUpdated, this.onCartUpdated);

      this.syncCartQuantityFromCart();
      this.syncCartState();
    }

    disconnectedCallback() {
      super.disconnectedCallback();

      this.buyButtonsBlock?.removeEventListener(
        "click",
        this.handleBuyButtonsClick,
      );

      if (this.isCartMode) {
        this.input.removeEventListener(
          "change",
          this.handleCartQuantityChange,
          true,
        );
        this.removeEventListener("click", this.handleCartStepperClick, true);
      }
    }

    get isCartMode() {
      return this.config.mode === "cart";
    }

    get buyButtonsBlock() {
      return this.closest(".product-details__block--buy-buttons");
    }

    get cartMessageElement() {
      return this.buyButtonsBlock?.querySelector(
        "[data-cart-availability-message]",
      );
    }

    get form() {
      return this.input.form || this.closest("form");
    }

    get variantId() {
      const { form } = this;

      if (form) {
        const id = new FormData(form).get("id");

        if (id) {
          return Number(id);
        }
      }

      return Number(this.config.variant);
    }

    get storeTotal() {
      if (this.StoreAvailability.locations.length > 0) {
        const live = this.StoreAvailability.quantityAvailable;

        if (live != null) {
          return Number(live);
        }

        return NaN;
      }

      if (this.dataset.quantityAvailable === undefined) {
        return NaN;
      }

      return Number(this.dataset.quantityAvailable);
    }

    get hasLoadedAvailability() {
      return (
        this.StoreAvailability.locations.length > 0 ||
        this.dataset.quantityAvailable !== undefined
      );
    }

    syncAvailabilityFromStore() {
      if (!this.StoreAvailability.locations.length) {
        return;
      }

      const { quantityAvailable } = this.StoreAvailability;

      if (quantityAvailable != null) {
        this.dataset.quantityAvailable = quantityAvailable;
      } else {
        delete this.dataset.quantityAvailable;
      }
    }

    get maxForThisLine() {
      if (!Number.isFinite(this.storeTotal)) {
        return NaN;
      }

      if (!this.isCartMode || !this.config.lineKey) {
        return this.storeTotal;
      }

      const siblingQty = WYATT.Cart.getSiblingQuantityForVariant(
        this.variantId,
        this.config.lineKey,
      );

      return Math.max(0, this.storeTotal - siblingQty);
    }

    get remainingAvailable() {
      if (!Number.isFinite(this.storeTotal)) {
        return NaN;
      }

      return Math.max(0, this.storeTotal - this.cartQuantity);
    }

    get maxAllowed() {
      if (!Number.isFinite(this.storeTotal)) {
        return NaN;
      }

      if (this.isCartMode) {
        return this.maxForThisLine;
      }

      return this.cartQuantity === 0
        ? this.storeTotal
        : this.remainingAvailable;
    }

    get errorMessage() {
      return WYATT.t.quantity_validation.error_message.replace(
        "{{ quantity }}",
        this.remainingAvailable,
      );
    }

    getPartialFulfillmentMessage() {
      return WYATT.t.quantity_validation.cart_partial_fulfillment;
    }

    getCartLimitMessage() {
      return WYATT.t.quantity_validation.error_message.replace(
        "{{ quantity }}",
        this.storeTotal,
      );
    }

    syncCartQuantityFromCart() {
      this.cartQuantity = WYATT.Cart.getQuantityForVariant(this.variantId);
    }

    handleCartUpdated() {
      this.syncCartQuantityFromCart();
      this.syncAvailabilityFromStore();
      this.syncCartState();
    }

    handlePreferredStoreChange(store) {
      this.validateGeneration += 1;
      this.dataset.storeNumber = store?.store_number;
      this.lastClampedValue = null;
      this.syncAvailabilityFromStore();
      this.syncCartQuantityFromCart();
      this.syncCartState();

      this.validate();
    }

    handleUpdateDOM(updates = []) {
      const productOptionsUpdated = updates.some(
        (update) =>
          typeof update.liveRegion === "string" &&
          update.liveRegion.startsWith("product-options-"),
      );
      const cartItemsUpdated =
        this.isCartMode &&
        updates.some((update) => update.liveRegion === "cart-items");

      if (productOptionsUpdated || cartItemsUpdated) {
        this.syncCartQuantityFromCart();
        this.validate();
      }
    }

    handleCartStepperClick(event) {
      const plusButton = event.target.closest('[name="plus"]');

      if (!plusButton || !this.contains(plusButton)) {
        return;
      }

      if (!this.hasLoadedAvailability) {
        event.preventDefault();
        event.stopImmediatePropagation();

        return;
      }

      const current = Number(this.input.value);

      if (Number.isFinite(this.storeTotal) && current >= this.maxForThisLine) {
        event.preventDefault();
        event.stopImmediatePropagation();

        createMessage(this.getCartLimitMessage(), {
          type: "error",
          minDelay: 5000,
        });
      }
    }

    get lineQuantityFromCart() {
      if (!this.isCartMode || !this.config.lineKey) {
        return this.cartQuantity;
      }

      return WYATT.Cart.getQuantityForLineKey(this.config.lineKey);
    }

    handleCartQuantityChange(event) {
      this.syncCartQuantityFromCart();

      if (!this.hasLoadedAvailability) {
        const cartQty = this.lineQuantityFromCart;
        const requested = Number(this.input.value);

        if (requested !== cartQty) {
          this.input.value = cartQty;
          this.querySelector("quantity-stepper")?.checkDisabledState?.();
          event.stopImmediatePropagation();
        }

        return;
      }

      const requested = Number(this.input.value);

      if (
        !Number.isFinite(this.maxForThisLine) ||
        requested <= this.maxForThisLine ||
        requested < this.lineQuantityFromCart
      ) {
        this.querySelector("quantity-stepper")?.checkDisabledState?.();

        return;
      }

      const fulfilled = Math.max(0, this.maxForThisLine);

      this.clampQuantity(fulfilled, { showMessage: true });
    }

    clampQuantity(fulfilled, { showMessage = false, dedupe = false } = {}) {
      const current = Number(this.input.value);

      if (current === fulfilled) {
        return false;
      }

      this.input.value = fulfilled;
      this.querySelector("quantity-stepper")?.checkDisabledState?.();

      if (showMessage) {
        const shouldShow = !dedupe || this.lastClampedValue !== fulfilled;

        if (shouldShow) {
          this.lastClampedValue = fulfilled;
          createMessage(this.getPartialFulfillmentMessage(), {
            type: "error",
            minDelay: 5000,
          });
        }
      }

      return true;
    }

    handleBuyButtonsClick(event) {
      if (event.target.closest("[data-view-cart]")) {
        this.handleViewCartClick(event);
      }
    }

    handleViewCartClick(event) {
      event.preventDefault();

      this.publish(WYATT.PubSub.events.addToCart);
    }

    handleSubmit(event) {
      if (this.isCartMode) {
        return;
      }

      /*
        If availability has not been checked yet, do not block the add-to-cart.
        (Error on the side of letting the user add to cart).
      */
      if (!this.dataset.quantityAvailable) {
        return;
      }

      if (this.buyButtonsBlock?.dataset.cartLimit === "reached") {
        event.preventDefault();
        event.stopImmediatePropagation();

        return;
      }

      const quantity = Number(this.input.value);
      const remaining = this.remainingAvailable;

      if (Number.isFinite(remaining) && quantity > remaining) {
        event.preventDefault();
        event.stopImmediatePropagation();

        createMessage(this.errorMessage, { type: "error", minDelay: 5000 });
      }
    }

    interpolateMessage(template, replacements) {
      return Object.entries(replacements).reduce(
        (message, [key, value]) =>
          message
            .replaceAll(`__${key.toUpperCase()}__`, value)
            .replace(new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, "gi"), value),
        template,
      );
    }

    getCartAvailabilityMessage() {
      if (this.cartQuantity === 0) {
        return null;
      }

      const count = String(this.cartQuantity);
      const remaining = String(this.remainingAvailable);
      const {
        cart_availability: cartAvailability,
        cart_max_reached: cartMaxReached,
      } = WYATT.t.product_form ?? {};

      if (this.remainingAvailable === 0) {
        return cartMaxReached ?? "All available stock in your cart";
      }

      if (cartAvailability) {
        return this.interpolateMessage(cartAvailability, { count, remaining });
      }

      return `${count} in your cart · ${remaining} more available`;
    }

    updateCartMessage() {
      const messageElement = this.cartMessageElement;

      if (!messageElement) {
        return false;
      }

      const message = this.getCartAvailabilityMessage();

      if (message) {
        messageElement.textContent = message;
        messageElement.removeAttribute("hidden");
      } else {
        messageElement.textContent = "";
        messageElement.setAttribute("hidden", "");
      }

      return true;
    }

    scheduleCartMessageSync() {
      if (this.pendingCartMessageSync) {
        return;
      }

      this.pendingCartMessageSync = true;

      requestAnimationFrame(this.flushCartMessageSync);
    }

    flushCartMessageSync() {
      this.pendingCartMessageSync = false;

      if (this.updateCartMessage()) {
        return;
      }

      requestAnimationFrame(() => {
        this.updateCartMessage();
      });
    }

    syncCartState() {
      if (this.isCartMode) {
        if (!Number.isFinite(this.maxForThisLine)) {
          return;
        }

        this.input.max = this.maxForThisLine;
        this.querySelector("quantity-stepper")?.checkDisabledState?.();

        return;
      }

      const buyBlock = this.buyButtonsBlock;

      if (!buyBlock || !Number.isFinite(this.storeTotal)) {
        return;
      }

      const remaining = this.remainingAvailable;
      const atLimit = this.cartQuantity > 0 && remaining === 0;

      if (atLimit) {
        buyBlock.dataset.cartLimit = "reached";
      } else {
        delete buyBlock.dataset.cartLimit;

        this.input.max = this.maxAllowed;

        if (remaining > 0 && Number(this.input.value) > remaining) {
          this.input.value = Math.max(1, remaining);
        }

        this.querySelector("quantity-stepper")?.checkDisabledState?.();
      }

      if (!this.updateCartMessage()) {
        this.scheduleCartMessageSync();
      }
    }

    async validate() {
      this.validateGeneration += 1;
      const generation = this.validateGeneration;

      await this.StoreAvailability.getAvailability();

      if (generation !== this.validateGeneration) {
        return;
      }

      this.syncAvailabilityFromStore();
      this.syncCartQuantityFromCart();
      this.syncCartState();
    }
  },
);
